import type { OutstandingOrderItem } from '@gc/shared';

import type { OutstandingLine, OutstandingSection } from './outstandingRows';

/**
 * The order a party's lines are listed in (owner, 2026-09-23: "when i select a
 * customer i should be able to sort their items/reservation for example by
 * 'Highest paying' or 'Lowest paying'").
 *
 * `newest` is the screen as it has always been — the default, and untouched:
 * the rows arrive newest first from `toOutstandingLines` and are not re-sorted.
 *
 * WHAT MOVES AND WHAT DOES NOT. The sort re-orders the lines inside each party
 * and the parties among themselves; it never re-cuts the sections, so a party
 * with two tabs is still two sections with two Pay buttons, and each header's
 * total is still what that Pay button charges.
 *
 * SELECTION IS UNTOUCHED BY ANY OF THIS. Every tick is keyed on a session id,
 * a bill id or an order-item id (see `selection.ts`), never on a position, so a
 * row that moves keeps its tick, and a partial item payment still splits off
 * exactly the lines that were ticked — in the server's own order, which
 * `groupSelectionByTab` reads from the payload rather than from the screen.
 */
export const OUTSTANDING_SORTS = ['newest', 'oldest', 'highest', 'lowest'] as const;
export type OutstandingSort = (typeof OUTSTANDING_SORTS)[number];

export const DEFAULT_OUTSTANDING_SORT: OutstandingSort = 'newest';

export function isOutstandingSort(value: unknown): value is OutstandingSort {
  return typeof value === 'string' && (OUTSTANDING_SORTS as readonly string[]).includes(value);
}

/** Money strings compared exactly — a float would tie two different large totals. */
function compareMd(a: string, b: string): number {
  const left = BigInt(a);
  const right = BigInt(b);
  return left === right ? 0 : left < right ? -1 : 1;
}

/**
 * Lines in the chosen order. Equal amounts fall back to newest first, then to
 * the id, so two identical coffees do not swap places on every poll.
 */
function compareLines(sort: OutstandingSort) {
  const newestFirst = (a: OutstandingLine, b: OutstandingLine) =>
    b.startedAt.localeCompare(a.startedAt) || a.id.localeCompare(b.id);
  switch (sort) {
    case 'newest':
      return newestFirst;
    case 'oldest':
      return (a: OutstandingLine, b: OutstandingLine) =>
        a.startedAt.localeCompare(b.startedAt) || a.id.localeCompare(b.id);
    case 'highest':
      return (a: OutstandingLine, b: OutstandingLine) =>
        compareMd(b.totalMd, a.totalMd) || newestFirst(a, b);
    case 'lowest':
      return (a: OutstandingLine, b: OutstandingLine) =>
        compareMd(a.totalMd, b.totalMd) || newestFirst(a, b);
  }
}

/**
 * Parties and their lines in the chosen order.
 *
 * By amount, a party ranks by its section total — "highest paying" across the
 * whole screen is the party that owes the most, which is also the figure its
 * header shows. By time, a party ranks by its first line in that order. Ties
 * keep the order the sections arrived in (the sort is stable).
 */
export function sortOutstandingSections(
  sections: readonly OutstandingSection[],
  sort: OutstandingSort,
): OutstandingSection[] {
  if (sort === DEFAULT_OUTSTANDING_SORT) return [...sections];

  const byLine = compareLines(sort);
  const sorted = sections.map((section) => ({
    ...section,
    lines: [...section.lines].sort(byLine),
  }));

  return sorted.sort((a, b) => {
    if (sort === 'highest') return a.totalMd === b.totalMd ? 0 : a.totalMd > b.totalMd ? -1 : 1;
    if (sort === 'lowest') return a.totalMd === b.totalMd ? 0 : a.totalMd < b.totalMd ? -1 : 1;
    const first = (section: OutstandingSection) => section.lines[0];
    const left = first(a);
    const right = first(b);
    return left && right ? byLine(left, right) : 0;
  });
}

/**
 * An order's item lines in the chosen order, by the figure each item row shows
 * (`owedMd`). Items carry no time of their own, so the time sorts keep the
 * order the server sent — the order the ticket printed in.
 */
export function sortOrderItems(
  items: readonly OutstandingOrderItem[],
  sort: OutstandingSort,
): OutstandingOrderItem[] {
  if (sort !== 'highest' && sort !== 'lowest') return [...items];
  const direction = sort === 'highest' ? -1 : 1;
  return [...items].sort((a, b) => direction * compareMd(a.owedMd, b.owedMd));
}

/** Remembered for the browser session: a cashier's choice survives a reload, not a new shift. */
export const OUTSTANDING_SORT_KEY = 'gc.outstanding.sort';

export function readStoredSort(storage: Pick<Storage, 'getItem'> | undefined): OutstandingSort {
  try {
    const stored = storage?.getItem(OUTSTANDING_SORT_KEY);
    return isOutstandingSort(stored) ? stored : DEFAULT_OUTSTANDING_SORT;
  } catch {
    // Storage can throw in a locked-down browser; the default is always fine.
    return DEFAULT_OUTSTANDING_SORT;
  }
}

export function storeSort(
  storage: Pick<Storage, 'setItem'> | undefined,
  sort: OutstandingSort,
): void {
  try {
    storage?.setItem(OUTSTANDING_SORT_KEY, sort);
  } catch {
    // Not remembered — the screen still sorts.
  }
}
